import React, { useMemo } from 'react'

import {
  ArrowTurnDownRightIcon,
  ClockIcon,
  EnvelopeIcon,
  LinkIcon,
  TrashIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline'
import { Form, Formik } from 'formik'
import { first, set } from 'lodash'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import ReactTooltip from 'react-tooltip'
import * as Yup from 'yup'

import { useCheckHasAlert } from '../../components/Alertsv2/useCheckHasAlert'
import { useAppSelector } from '@/client/hooks'
import CallPhone from '@/components/CallPhone'
import { InputField, SelectField } from '@/components/FormikInputs'
import MaskedSSNNUmberField from '@/components/FormikInputs/MaskedSSNNUmberField'
import { ProfileTypes, TitleTypeOptions } from '@/constants/applicationProfile'
import { AlertFlag } from '@/features/AlertFlag/AlertFlag'
import { RedFlagIcon } from '@/features/AlertFlag/RedFlagIcon'
import { CustomerMagicLinkButton } from '@/features/FileContacts/CustomerMagicLinkButton'
import { useNameManager } from '@/features/FileContacts/hooks/contactNameManager'
import { useApplicantsAndContactProfiles } from '@/features/FileContacts/useApplicants'
import useCardsCollapseStatus from '@/features/FileContacts/useCardsCollapseStatus'
import { generateMarriedOptions, serializeNameDetails } from '@/features/FileContacts/utils'
import { Application } from '@/graphql/generated'
import { useGetApplicationQuery } from '@/graphql/services/application'
import { useCreateAddressMutation, useUpdateAddressMutation } from '@/services/address'
import { useGetApplicationSsnsQuery } from '@/services/application'
import { useGetSsnUnmaskedQuery, useUpdateProfileMutation } from '@/services/applicationProfile'
import { useGetLatestTitleReportMetadataQuery } from '@/services/documentMetadata'
import {
  useDeleteNonApplicantMutation,
  useListApplicationProfilesQuery,
  useListNonApplicantsQuery,
  useSwitchProfileTypeMutation,
  useUpdateNonApplicantMutation,
} from '@/services/v2-application/applicationProfile'
import { ApplicationProfile } from '@/types/unlock'
import { FEATURES_LIST } from '@/utils/features/featuresList'
import { useFeatureFlag } from '@/utils/features/splitSdk'
import { useFormIkHelper } from '@/utils/formIk'
import { socialSecurityNumberValidation } from '@/utils/ssnValidation'
import { toastDefaultOptions } from '@/utils/toast'

interface ApplicantCardProps {
  applicationProfile: ApplicationProfile
  application: Application
  shouldBeLocked: boolean
  handleSendEmail: (recipient: string) => void
  type: string
}

export function NonApplicantCard({
  applicationProfile,
  application,
  handleSendEmail,
  shouldBeLocked,
  type,
}: ApplicantCardProps) {
  const router = useRouter()
  const { id } = router.query

  const { data: applicationProfilesData } = useListApplicationProfilesQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const applicationProfiles = applicationProfilesData?.results

  const { data: nonApplicantsData } = useListNonApplicantsQuery(application?.uuid, {
    skip: !application?.uuid,
  })
  const nonApplicants = nonApplicantsData?.results

  const { data: applicationSsns } = useGetApplicationSsnsQuery(application?.externalId, {
    skip: !application?.uuid,
  })
  const { data: titleReportMetadata } = useGetLatestTitleReportMetadataQuery(application?.uuid, {
    skip: !application?.uuid,
  })

  const { data: ssn } = useGetSsnUnmaskedQuery(applicationProfile?.profile?.uuid, {
    skip: !applicationProfile?.profile?.uuid,
  })

  const ssnUnmasked = ssn?.social_security_number?.replaceAll('-', '')

  const [updateProfile] = useUpdateProfileMutation()
  const [createAddress] = useCreateAddressMutation()
  const [updateAddress] = useUpdateAddressMutation()
  const [updateNonApplicant] = useUpdateNonApplicantMutation()
  const [deleteNonApplicant] = useDeleteNonApplicantMutation()
  const [switchProfileType, { isLoading }] = useSwitchProfileTypeMutation()
  const { data: applicationData } = useGetApplicationQuery(
    { externalId: id as string },
    { skip: !id }
  )

  const alertsTypes = useAppSelector((state) => state?.general?.alertsTypes)
  const { isExpanded, toggleIsExpanded } = useCardsCollapseStatus(true)

  const isNonApplicantNameFieldsEnabled = useFeatureFlag(FEATURES_LIST.NON_APPLICANT_NAME_FIELDS)

  const { emailIsDuplicated } = useApplicantsAndContactProfiles(application?.uuid)

  const { validate } = useFormIkHelper()

  const { ownerNamesOptions, onUpdateName } = useNameManager(
    applicationProfile,
    titleReportMetadata,
    updateProfile
  )

  const profileFullName = applicationProfile?.profile?.full_name?.toUpperCase()?.trim() || ''
  const { key: alertKeyTitleVestingNameMismatch, hasAlert: hasAlertTitleVestingNamesMismatch } =
    useCheckHasAlert({
      key: `suspicious-data-title-vesting-names-mismatch-non-applicant-${profileFullName}`,
    })

  const isCoOwner = useMemo(
    () => applicationProfile.profile_type === ProfileTypes.CO_OWNER,
    [applicationProfile]
  )

  const isNOO = useMemo(
    () => applicationProfile.profile_type === ProfileTypes.NOO,
    [applicationProfile]
  )

  const showNamesPicklist = (() => {
    if (isNOO) {
      // If NOO (Non-Owner Occupied), names picklist should not be shown
      return false
    }

    // Show the picklist if non-applicant name fields are enabled, otherwise return false
    return isNonApplicantNameFieldsEnabled
      ? !!titleReportMetadata?.owners_of_record_names?.length
      : false
  })()

  const getSsnValue = (ssn) => {
    if (/^\d{9}$/.test(ssn) && !ssn.includes('*')) {
      return ssn
    }
  }

  const onUpdateAppProfile = async (values, setFieldTouched) => {
    try {
      const { type, married_to_profile, title_type, excluded_from_signatures } = values

      const fields = ['married_to_profile']
      await validate(fields, setFieldTouched)

      const payload = {
        type,
        married_to_profile: eval(married_to_profile),
        title_type,
        excluded_from_signatures,
      }
      toast.dismiss({ containerId: 'global' })
      updateNonApplicant({
        applicationUuid: application?.uuid,
        nonApplicantUuid: applicationProfile?.uuid,
        ...payload,
      })
        .unwrap()
        .then(() => {
          toast.success('The profile has been successfully updated.', { ...toastDefaultOptions })
        })
        .catch((e) => {
          const message = e?.data ? e?.data?.[0] : 'Something went wrong'
          toast.error(message, { ...toastDefaultOptions })
        })
    } catch (err) {
      console.error(err)
    }
  }

  const onUpdateProfile = async (values, touchField) => {
    try {
      const { profile } = values

      const fields = ['profile.first_name', 'profile.last_name', 'profile.email']
      await validate(fields, touchField)

      const payload = profile

      // Safely handle SSN value extraction and assignment
      const ssn = getSsnValue(profile.social_security_number)
      if (ssn) {
        payload.social_security_number = ssn
      } else {
        delete payload.social_security_number
      }

      payload.photo_id_expire = profile?.photo_id_expire || null
      payload.born_on = profile?.born_on || null

      toast.dismiss({ containerId: 'global' })
      updateProfile(payload)
        .unwrap()
        .then(() => {
          toast.success('The profile has been successfully updated.', { ...toastDefaultOptions })
        })
        .catch((error) => {
          const message = error?.data?.[0] || 'Error: Something went wrong'
          toast.error(message, { ...toastDefaultOptions })
        })
    } catch (err) {
      console.error(err)
    }
  }

  const handleAddress = async (values, oldValues, touchField) => {
    try {
      const fields = ['address.street', 'address.city', 'address.zip_code', 'address.state']
      await validate(fields, touchField)

      if (!values?.uuid) {
        return createAddress({ address: values })
          .unwrap()
          .then((data) => {
            updateProfile({
              uuid: initialValues?.profile?.uuid,
              mailing_address_id: data.id,
            })
            toast.success('The address has been successfully created.', { ...toastDefaultOptions })
          })
          .catch(() => {
            toast.error('Something went wrong.', { ...toastDefaultOptions })
          })
      }

      const hasChanges = JSON.stringify(values) !== JSON.stringify(oldValues)
      if (hasChanges) {
        updateAddress(values)
          .unwrap()
          .then(() => {
            toast.success('The address has been successfully updated.', { ...toastDefaultOptions })
          })
          .catch(() => {
            toast.error('Something went wrong.', { ...toastDefaultOptions })
          })
      }
    } catch (err) {
      console.error(err)
    }
  }

  const handleDeleteNonApplicant = async (applicationUuid, nonApplicantUuid) => {
    deleteNonApplicant({ applicationUuid, nonApplicantUuid })
      .unwrap()
      .then(() => {
        toast.success('Contact has been successfully deleted.', { ...toastDefaultOptions })
      })
      .catch(() => {
        toast.error('Something went wrong.', { ...toastDefaultOptions })
      })
  }

  const marriedToOptions = useMemo(() => {
    return generateMarriedOptions(applicationProfile, applicationProfiles, nonApplicants)
  }, [applicationProfiles, nonApplicants])

  const validationSchema = Yup.object().shape({
    married_to_profile: Yup.string(),
    profile: Yup.object({
      first_name: Yup.string().required('First name is required'),
      last_name: Yup.string().required('Last name is required'),
      social_security_number: socialSecurityNumberValidation(
        ssnUnmasked,
        applicationSsns,
        application?.applicationState ?? ''
      )?.optional(),
      email: Yup.string()
        .required('Email is required')
        .email('Email must be valid.')
        .test(
          'not-repeated-email',
          'Email address already exists and non-applicant can only share their spouse’s email.',
          function (value) {
            // If no value provided, skip further checks
            if (!value) return true

            return !emailIsDuplicated(applicationProfile, value)
          }
        ),
      is_us_citizen_or_green_card_holder: Yup.boolean().nullable().optional(),
      photo_id_expire: Yup.date().nullable().optional(),
      born_on: Yup.date().nullable().optional(),
    }),
    address: Yup.object({
      street: Yup.string().required('Street is required'),
      city: Yup.string().required('City is required'),
      state: Yup.string().max(2).required('State is required'),
      zip_code: Yup.string().required('Zip code is required'),
    }),
  })

  const initialValues = {
    type: applicationProfile?.profile_type || '',
    married_to_profile: applicationProfile?.married_to_profile || '',
    profile: {
      uuid: applicationProfile?.profile?.uuid || '',
      email: applicationProfile?.profile?.email || '',
      phone: applicationProfile?.profile?.phone || '',
      first_name: applicationProfile?.profile?.first_name || '',
      middle_name: applicationProfile?.profile?.middle_name || '',
      last_name: applicationProfile?.profile?.last_name || '',
      full_name: serializeNameDetails(applicationProfile?.profile) || '',
      born_on: applicationProfile?.profile?.born_on || null,
      social_security_number:
        applicationProfile?.profile?.social_security_number?.replaceAll('-', '') || '',
      is_us_citizen_or_green_card_holder:
        applicationProfile?.profile?.is_us_citizen_or_green_card_holder,
      photo_id_expire: applicationProfile?.profile?.photo_id_expire || null,
    },
    address: {
      uuid: applicationProfile?.profile?.mailing_address?.uuid || '',
      street: applicationProfile?.profile?.mailing_address?.street || '',
      unit_number: applicationProfile?.profile?.mailing_address?.unit_number || '',
      city: applicationProfile?.profile?.mailing_address?.city || '',
      state: applicationProfile?.profile?.mailing_address?.state || '',
      zip_code: applicationProfile?.profile?.mailing_address?.zip_code || '',
    },
    title_type: applicationProfile?.title_type || '',
    excluded_from_signatures: applicationProfile?.excluded_from_signatures || false,
  }

  const alerts: Partial<Record<string, string>> = {}

  // Error and touched state setting
  const computeAlerts = (initialValues, alerts: Record<string, string>, alertsTypes: string[]) => {
    const checkAndSetAlert = (fieldValue: string, fieldName: string, alertType: string) => {
      if (!fieldValue && alertsTypes?.includes(alertType)) {
        alerts[fieldName] = alertType
      }
    }

    checkAndSetAlert(
      initialValues.profile.social_security_number,
      'profile.social_security_number',
      'missing-data-non-applicant-ssn'
    )
    checkAndSetAlert(
      initialValues.profile.email,
      'profile.email',
      'missing-data-non-applicant-valid-email-address'
    )
    checkAndSetAlert(initialValues.address.street, 'address.street', 'missing-data-co-owner-street')
    checkAndSetAlert(initialValues.address.city, 'address.city', 'missing-data-co-owner-city')
    checkAndSetAlert(initialValues.address.state, 'address.state', 'missing-data-co-owner-state')
    checkAndSetAlert(
      initialValues.address.zip_code,
      'address.zip_code',
      'missing-data-co-owner-zipcode'
    )
  }

  computeAlerts(initialValues, alerts, alertsTypes)

  const showAlertFlag = useMemo(() => Object.keys(alerts).length > 0, [alerts])

  const nameFlags =
    isCoOwner && !applicationProfile.matches_title
      ? ['suspicious-co-owner-names-dont-match-title-report']
      : []

  if (hasAlertTitleVestingNamesMismatch) {
    nameFlags.push(alertKeyTitleVestingNameMismatch)
  }

  nameFlags.push(`missing-data-co-owner-missing-full-name_${applicationProfile?.id}`)
  nameFlags.push(`missing-data-noo-full-name-missing_${applicationProfile?.id}`)

  return (
    <div className='card bg-base-100 shadow-md flex flex-col border'>
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        onSubmit={() => {}}
        enableReinitialize
        validateOnMount
      >
        {({ values, setFieldValue, errors, touched, setFieldTouched, validateField }) => {
          return (
            <Form>
              <div
                onClick={() => toggleIsExpanded()}
                className={`w-full gap-2 flex justify-between px-5 py-2 ${
                  isExpanded && 'bg-[#24d3ee]'
                } transition-colors duration-500 rounded-t-box  cursor-pointer`}
              >
                <div className='w-full flex flex-col text-start'>
                  <SelectField
                    extraFieldClasses={`${isExpanded && 'bg-[#24d3ee]'} ${
                      !isExpanded && 'pointer-events-none'
                    } transition-colors duration-500 !text-black !pl-0 !pt-0 !pb-0`}
                    extraClasses='w-full'
                    placeholder='Contact Type'
                    name='type'
                    options={[
                      { label: 'Co-Applicant', value: ProfileTypes.APPLICANT },
                      { label: 'Co-Owner', value: ProfileTypes.CO_OWNER },
                      { label: 'NOO', value: ProfileTypes.NOO },
                    ]}
                    showError
                    onCustomClick={(e) => {
                      if (isExpanded) e.stopPropagation()
                    }}
                    onChange={async (e) => {
                      try {
                        await switchProfileType({
                          applicationUuid: application.uuid,
                          applicantProfileUuid: `${applicationProfile.uuid}`,
                          new_type: e.target.value,
                        })
                      } catch (error) {
                        toast.error(error, { ...toastDefaultOptions })
                      }
                    }}
                    disabled={
                      isLoading || applicationProfile?.profile?.full_name === '' || shouldBeLocked
                    }
                  />
                </div>
                <div className='flex items-center gap-2'>
                  {!isExpanded && (
                    <>
                      {isCoOwner && !isExpanded && showAlertFlag && <RedFlagIcon />}

                      <div
                        className='tooltip tooltip-left'
                        data-tip={`Phone: ${values?.profile?.phone}`}
                      >
                        <CallPhone callTo={values?.profile?.phone} />
                      </div>
                      <div
                        className='tooltip tooltip-left'
                        data-tip={`E-mail: ${values?.profile?.email}`}
                      >
                        <EnvelopeIcon
                          className='w-4 cursor-pointer'
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSendEmail(values?.profile?.email)
                          }}
                        />
                      </div>
                    </>
                  )}
                  {!isExpanded && (
                    <TrashIcon
                      className='w-4 hover:text-error cursor-pointer transition-all'
                      onClick={(e) => {
                        e.stopPropagation()
                        handleDeleteNonApplicant(application?.uuid, applicationProfile?.uuid)
                      }}
                    />
                  )}
                </div>
              </div>
              <div className={`px-5 pb-2 pt-0 flex flex-col ${isExpanded && 'pt-2'}`}>
                <div className='flex flex-row gap-1 text-xs mb-1 items-center'>
                  <UserCircleIcon className='w-4' />
                  {showNamesPicklist ? (
                    <SelectField
                      extraClasses='w-full'
                      placeholder='Contact name'
                      name='profile.full_name'
                      options={ownerNamesOptions}
                      onBlur={(event) => onUpdateName(event)}
                      showError
                    />
                  ) : (
                    <InputField
                      name='profile.first_name'
                      placeholder='First Name'
                      showError
                      style='minimal'
                      parentClassName='w-full'
                      customClasses='!input !input-bordered'
                      onBlur={() => onUpdateProfile(values, setFieldTouched)}
                      disabled={shouldBeLocked}
                    />
                  )}
                  {!showNamesPicklist && nameFlags.length > 0 && (
                    <AlertFlag
                      flag={nameFlags}
                      position='left'
                    />
                  )}
                </div>
                {isExpanded && (
                  <>
                    {!showNamesPicklist && (
                      <div className='flex flex-row gap-1 text-xs items-center'>
                        <InputField
                          name='profile.middle_name'
                          placeholder='Middle Name'
                          parentClassName='w-full'
                          customClasses='!input !input-bordered'
                          onBlur={() => onUpdateProfile(values, setFieldTouched)}
                          disabled={shouldBeLocked}
                        />
                        <InputField
                          name='profile.last_name'
                          showError
                          placeholder='Last Name'
                          parentClassName='w-full'
                          customClasses='!input !input-bordered'
                          onBlur={() => onUpdateProfile(values, setFieldTouched)}
                          disabled={shouldBeLocked}
                        />
                        <AlertFlag
                          flag={[
                            `missing-data-co-owner-missing-full-name_${applicationProfile?.id}`,
                            `missing-data-noo-full-name-missing_${applicationProfile?.id}`,
                          ]}
                          position='left'
                        />
                      </div>
                    )}
                    {applicationData?.property?.titleHeld === 'trust' &&
                    applicationData?.pricingVersion >= 22 &&
                    type === ProfileTypes.CO_OWNER ? (
                      <div className='flex flex-row gap-1 text-xs'>
                        <ArrowTurnDownRightIcon className='w-4' />
                        <SelectField
                          extraClasses='w-full'
                          style='minimal-input'
                          placeholder='Title Type'
                          name='title_type'
                          options={TitleTypeOptions}
                          onBlur={() => onUpdateAppProfile(values, setFieldTouched)}
                          showError
                        />
                      </div>
                    ) : null}
                    <div className='flex flex-row gap-1 text-xs items-center'>
                      <LinkIcon className='w-4' />
                      <SelectField
                        extraClasses='w-full mt-1'
                        style='minimal-input'
                        placeholder='Married To'
                        placeholderDefaultValue={-1}
                        name='married_to_profile'
                        options={[...marriedToOptions]}
                        onBlur={() => onUpdateAppProfile(values, setFieldTouched)}
                        showError
                        disabled={shouldBeLocked}
                      />
                      <AlertFlag
                        flag={[
                          `missing-data-co-owner-married-to-missing_${applicationProfile?.id}`,
                          `missing-data-noo-married-to-missing_${applicationProfile?.id}`,
                        ]}
                        position='left'
                      />
                    </div>
                    <div className='flex flex-row gap-1 text-xs items-center'>
                      <InputField
                        name='profile.born_on'
                        type='date'
                        onBlur={() => onUpdateProfile(values, setFieldTouched)}
                        placeholder='Date of Birth'
                        parentClassName='w-full mt-1'
                        customClasses='!input !input-bordered'
                        disabled={shouldBeLocked}
                      />
                      <AlertFlag
                        flag={`missing-data-co-owner-dob-missing_${applicationProfile?.id}`}
                        position='left'
                      />
                    </div>
                    <div className='flex flex-row gap-1 text-xs items-center mt-1'>
                      <MaskedSSNNUmberField
                        placeholder='SSN'
                        name='profile.social_security_number'
                        onBlur={() => {
                          if (errors['profile']?.['social_security_number']) {
                            return
                          }
                          onUpdateProfile(values, setFieldTouched)
                        }}
                        format='###-##-####'
                        maskedFormat='***-**-####'
                        profile={applicationProfile?.profile}
                        type='text'
                        isNumericString={true}
                        setFieldValue={setFieldValue}
                        parentClassName='w-full'
                        customRight='right-1'
                        app={application?.externalId}
                        showError
                        errors={errors}
                        touched={touched}
                        afterOnChange={() => {
                          setFieldTouched('profile.social_security_number', true)
                          setTimeout(() => {
                            validateField('profile.social_security_number')
                          }, 0)
                        }}
                        disabled={shouldBeLocked}
                        style='classic-input'
                      />
                      <AlertFlag
                        flag={`missing-data-co-owner-ssn-missing_${applicationProfile?.id}`}
                        position='left'
                      />
                    </div>
                    <div className='flex flex-row gap-1 text-xs items-center mt-1'>
                      <CallPhone callTo={values?.profile?.phone} />
                      <InputField
                        name='profile.phone'
                        onBlur={() => onUpdateProfile(values, setFieldTouched)}
                        placeholder='Phone'
                        parentClassName='w-full'
                        customClasses='!input !input-bordered'
                        disabled={shouldBeLocked}
                      />
                      <AlertFlag
                        flag={`missing-data-co-owner-phone-missing_${applicationProfile?.id}`}
                        position='left'
                      />
                    </div>
                    <div className='flex flex-row gap-1 text-xs items-center mt-1'>
                      <EnvelopeIcon
                        className='w-4 cursor-pointer'
                        onClick={() => handleSendEmail(values?.profile?.email)}
                      />
                      <InputField
                        name='profile.email'
                        onBlur={() => onUpdateProfile(values, setFieldTouched)}
                        showError
                        placeholder='E-mail'
                        parentClassName='w-full'
                        disabled={shouldBeLocked}
                        customClasses='!input !input-bordered'
                      />
                      <AlertFlag
                        flag={[
                          `missing-data-co-owner-email-missing_${applicationProfile?.id}`,
                          `missing-data-noo-email-missing_${applicationProfile?.id}`,
                        ]}
                        position='left'
                      />
                    </div>
                    <div className='flex flex-row gap-1 text-xs mt-1'>
                      <InputField
                        data-for='street-input'
                        data-tip='street-input'
                        parentClassName='w-full'
                        label='Street'
                        name='address.street'
                        showError
                        onBlur={() =>
                          handleAddress(values.address, initialValues.address, setFieldTouched)
                        }
                        customClasses='!input !input-bordered'
                        labelFlag={
                          <AlertFlag
                            flag={[
                              'missing-data-co-owner-city',
                              `missing-data-co-owner-current-residence-incomplete_${applicationProfile?.id}`,
                            ]}
                          />
                        }
                        data-test='street-input'
                        disabled={shouldBeLocked}
                      />
                    </div>
                    <div className='flex flex-row gap-1 text-xs'>
                      <InputField
                        parentClassName='w-full'
                        label='Unit Number'
                        name='address.unit_number'
                        customClasses='!input !input-bordered'
                        onBlur={() =>
                          handleAddress(values.address, initialValues.address, setFieldTouched)
                        }
                        data-test='unit-number-input'
                        labelFlag={
                          <AlertFlag
                            flag={`missing-data-co-owner-unit-number-missing_${applicationProfile?.id}`}
                            position='left'
                          />
                        }
                      />
                      <InputField
                        parentClassName='w-full'
                        label='City'
                        name='address.city'
                        showError
                        customClasses='!input !input-bordered'
                        onBlur={() =>
                          handleAddress(values.address, initialValues.address, setFieldTouched)
                        }
                        labelFlag={
                          <AlertFlag
                            flag={[
                              'missing-data-co-owner-city',
                              `missing-data-co-owner-current-residence-incomplete_${applicationProfile?.id}`,
                            ]}
                            position='left'
                          />
                        }
                        data-test='city-input'
                        disabled={shouldBeLocked}
                      />
                    </div>
                    <div className='flex flex-row gap-1 text-xs'>
                      <InputField
                        parentClassName='w-full'
                        label='State'
                        showError
                        name='address.state'
                        onBlur={() =>
                          handleAddress(values.address, initialValues.address, setFieldTouched)
                        }
                        labelFlag={
                          <AlertFlag
                            flag={[
                              'missing-data-co-owner-city',
                              `missing-data-co-owner-current-residence-incomplete_${applicationProfile?.id}`,
                            ]}
                          />
                        }
                        customClasses='!input !input-bordered'
                        data-test='state-input'
                        disabled={shouldBeLocked}
                      />
                      <InputField
                        parentClassName='w-full'
                        label='Zip Code'
                        showError
                        name='address.zip_code'
                        onBlur={() =>
                          handleAddress(values.address, initialValues.address, setFieldTouched)
                        }
                        labelFlag={
                          <AlertFlag
                            flag={[
                              'missing-data-co-owner-city',
                              `missing-data-co-owner-current-residence-incomplete_${applicationProfile?.id}`,
                            ]}
                          />
                        }
                        data-test='zipcode-input'
                        disabled={shouldBeLocked}
                        customClasses='!input !input-bordered'
                      />
                    </div>
                    <div className='flex flex-row gap-1 text-xs items-center mt-1'>
                      <SelectField
                        extraClasses='w-full'
                        placeholder='U.S. Citizen/Has Green Card'
                        name='profile.is_us_citizen_or_green_card_holder'
                        extraFieldClasses={
                          values?.profile?.is_us_citizen_or_green_card_holder ||
                          values?.profile?.is_us_citizen_or_green_card_holder === false
                            ? 'text-inherit'
                            : 'text-grey-500'
                        }
                        options={[
                          { label: 'US Citizen/Has Green Card - Yes', value: true },
                          { label: 'US Citizen/Has Green Card - No', value: false },
                        ]}
                        onBlur={() => onUpdateProfile(values, setFieldTouched)}
                        showError
                        disabled={shouldBeLocked}
                        style='minimal-input'
                      />
                      {values?.type === ProfileTypes.CO_OWNER &&
                        !values?.profile.is_us_citizen_or_green_card_holder && (
                          <AlertFlag
                            flag={['bounds-co-owner-is-not-us-citizen']}
                            tooltip='Co-Owner is not a US Citizen or a Permanent Resident.'
                            position='left'
                          />
                        )}
                      <AlertFlag
                        flag={`missing-data-co-owner-missing-citizenship-declaration_${applicationProfile?.id}`}
                      />
                    </div>
                    <div className='flex flex-row gap-1 text-xs items-center mt-1'>
                      <InputField
                        name='profile.photo_id_expire'
                        type='date'
                        onBlur={() => onUpdateProfile(values, setFieldTouched)}
                        placeholder='Photo ID Expire'
                        parentClassName='w-full'
                        showError
                        customClasses='!input !input-bordered'
                      />
                      <AlertFlag
                        flag={[
                          `missing-data-co-owner-photo-id-expiration-missing_${applicationProfile?.id}`,
                          `missing-data-noo-photo-id-expiration-missing_${applicationProfile?.id}`,
                        ]}
                        position='left'
                      />
                      <AlertFlag
                        flag={`bounds-photo-id-expired_${applicationProfile?.profile?.id}`}
                        tooltip='This document has expired'
                        position='left'
                      />
                      <AlertFlag
                        flag={`bounds-photo-id-expires-in-n-days-non-applicants_${applicationProfile?.profile?.id}`}
                        icon={
                          <ClockIcon
                            width={18}
                            height={18}
                            color='black'
                            className='ml-2'
                          />
                        }
                        position='left'
                      />
                    </div>
                    {values?.type === ProfileTypes.NOO && (
                      <div className='flex flex-row gap-1 text-xs items-center'>
                        <SelectField
                          extraClasses='w-full'
                          placeholder='Exclude Signing'
                          name='excluded_from_signatures'
                          extraFieldClasses={
                            values?.excluded_from_signatures ||
                            values?.excluded_from_signatures === false
                              ? 'text-inherit'
                              : 'text-grey-500'
                          }
                          options={[
                            { label: 'Exclude Signing - Yes', value: true },
                            { label: 'Exclude Signing - No', value: false },
                          ]}
                          onBlur={() => onUpdateAppProfile(values, setFieldTouched)}
                          showError
                          disabled={shouldBeLocked}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </Form>
          )
        }}
      </Formik>
      <CustomerMagicLinkButton applicationProfile={applicationProfile} />
    </div>
  )
}
